// pages/admin/VentaDirecta.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bus, Calendar, MapPin, Users, Search, 
  ArrowRight, User, Phone, CheckCircle, 
  CreditCard, Ticket, Clock, ChevronLeft, ChevronRight,
  DollarSign, IdCard
} from 'lucide-react';
import Swal from 'sweetalert2';
import { pdf } from '@react-pdf/renderer';
import AdminLayout from '../../layouts/AdminLayout';
import { serviciosAPI } from '../../services/api';
import TicketVentaPDF from '../../components/TicketVentaPDF';

const API_URL = process.env.REACT_APP_API_URL;

// Ciudades hardcodeadas
const ciudades = [
  { id: 1, nombre: "Ciudad Bolívar", estado: "Bolívar" },
  { id: 2, nombre: "Puerto Ordaz", estado: "Bolívar" },
  { id: 3, nombre: "San Félix", estado: "Bolívar" },
  { id: 4, nombre: "Upata", estado: "Bolívar" },
  { id: 5, nombre: "Guasipati", estado: "Bolívar" },
  { id: 6, nombre: "El Callao", estado: "Bolívar" },
  { id: 7, nombre: "Tumeremo", estado: "Bolívar" },
  { id: 8, nombre: "Santa Elena de Uairén", estado: "Bolívar" },
  { id: 9, nombre: "Kavak", estado: "Bolívar" },
  { id: 10, nombre: "San Ignacio de Yuruaní", estado: "Bolívar" },
  { id: 11, nombre: "El Paují", estado: "Bolívar" },
  { id: 12, nombre: "Ciudad Guayana", estado: "Bolívar" },
  { id: 13, nombre: "Maturín", estado: "Monagas" },
  { id: 14, nombre: "El Tigre", estado: "Anzoátegui" },
  { id: 15, nombre: "San Tomé", estado: "Anzoátegui" },
  { id: 16, nombre: "Cantaura", estado: "Anzoátegui" },
  { id: 17, nombre: "Anaco", estado: "Anzoátegui" },
  { id: 18, nombre: "Barcelona", estado: "Anzoátegui" },
  { id: 19, nombre: "Puerto La Cruz", estado: "Anzoátegui" },
  { id: 20, nombre: "Lechería", estado: "Anzoátegui" },
  { id: 21, nombre: "Cumaná", estado: "Sucre" },
  { id: 22, nombre: "Carúpano", estado: "Sucre" },
  { id: 23, nombre: "Porlamar", estado: "Nueva Esparta" },
  { id: 24, nombre: "La Asunción", estado: "Nueva Esparta" },
  { id: 25, nombre: "Caracas", estado: "Distrito Capital" },
  { id: 26, nombre: "Maracay", estado: "Aragua" },
  { id: 27, nombre: "Valencia", estado: "Carabobo" },
  { id: 28, nombre: "Barquisimeto", estado: "Lara" },
  { id: 29, nombre: "Maracaibo", estado: "Zulia" },
  { id: 30, nombre: "Boa vista", estado: "Roraima - Brasil" },
  { id: 31, nombre: "Manaos", estado: "Amazonas - Brasil" }
];

const VentaDirecta = () => {
  const navigate = useNavigate();
  
  // Estado de búsqueda
  const [step, setStep] = useState(1); // 1: Búsqueda, 2: Servicios, 3: Datos, 4: Confirmar
  const [busquedaData, setBusquedaData] = useState({
    origen: '',
    destino: '',
    fecha: '',
    pasajeros: 1
  });
  const [servicios, setServicios] = useState([]);
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estado para sugerencias
  const [origenSugerencias, setOrigenSugerencias] = useState([]);
  const [destinoSugerencias, setDestinoSugerencias] = useState([]);
  const [showOrigenSugerencias, setShowOrigenSugerencias] = useState(false);
  const [showDestinoSugerencias, setShowDestinoSugerencias] = useState(false);
  
  // Estado para datos de pasajeros
  const [cantidadPasajeros, setCantidadPasajeros] = useState(1);
  const [passengerData, setPassengerData] = useState([]);
  const [currentPassengerIndex, setCurrentPassengerIndex] = useState(0);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const filtrarCiudades = (texto) => {
    if (!texto) return [];
    return ciudades
      .filter(ciudad => 
        ciudad.nombre.toLowerCase().includes(texto.toLowerCase()) ||
        ciudad.estado.toLowerCase().includes(texto.toLowerCase())
      )
      .slice(0, 8);
  };

  const handleOrigenChange = (e) => {
    const value = e.target.value;
    setBusquedaData({...busquedaData, origen: value});
    setOrigenSugerencias(filtrarCiudades(value));
    setShowOrigenSugerencias(value.length > 0);
  };

  const handleDestinoChange = (e) => {
    const value = e.target.value;
    setBusquedaData({...busquedaData, destino: value});
    setDestinoSugerencias(filtrarCiudades(value));
    setShowDestinoSugerencias(value.length > 0);
  };

  const seleccionarOrigen = (ciudad) => {
    setBusquedaData({...busquedaData, origen: ciudad.nombre});
    setShowOrigenSugerencias(false);
  };

  const seleccionarDestino = (ciudad) => {
    setBusquedaData({...busquedaData, destino: ciudad.nombre});
    setShowDestinoSugerencias(false);
  };

  const esFechaFutura = (fecha) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaComparar = new Date(fecha);
    fechaComparar.setHours(0, 0, 0, 0);
    return fechaComparar >= hoy;
  };

  // Buscar servicios
  const buscarServicios = async () => {
    if (!busquedaData.origen || !busquedaData.destino || !busquedaData.fecha) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Completá origen, destino y fecha'
      });
      return;
    }
    
    setLoading(true);
    try {
      const data = await serviciosAPI.buscar(busquedaData.origen, busquedaData.destino, busquedaData.fecha);
      setServicios(data);
      await cargarFechasDisponibles();
      irAlPaso(2);
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se encontraron servicios' });
    } finally {
      setLoading(false);
    }
  };

  const cargarFechasDisponibles = async () => {
    try {
      const data = await serviciosAPI.getFechasDisponibles(busquedaData.origen, busquedaData.destino);
      const fechasFuturas = data.filter(item => esFechaFutura(item.fecha));
      const fechasFormateadas = fechasFuturas.map(item => ({
        fecha: item.fecha,
        dia: new Date(item.fecha).toLocaleDateString('es', { weekday: 'short' }),
        diaNumero: new Date(item.fecha).getDate(),
        mes: new Date(item.fecha).toLocaleDateString('es', { month: 'short' }),
        precioMinimo: item.precioMinimo,
        tieneServicios: item.cantidad > 0
      }));
      setFechasDisponibles(fechasFormateadas);
      
      const index = fechasFormateadas.findIndex(f => f.fecha === busquedaData.fecha);
      if (index !== -1) setSelectedDateIndex(index);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDateChange = async (index) => {
    setSelectedDateIndex(index);
    setLoading(true);
    const fechaSeleccionada = fechasDisponibles[index];
    if (fechaSeleccionada) {
      const data = await serviciosAPI.buscar(busquedaData.origen, busquedaData.destino, fechaSeleccionada.fecha);
      setServicios(data);
      setBusquedaData({...busquedaData, fecha: fechaSeleccionada.fecha});
    }
    setLoading(false);
  };

  const irAlPaso = (nuevoPaso) => {
    setStep(nuevoPaso);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pasoAnterior = () => {
    if (step > 1) {
      irAlPaso(step - 1);
    }
  };

  // Seleccionar servicio
  const seleccionarServicio = async (servicio) => {
    setSelectedServicio(servicio);
    setCantidadPasajeros(busquedaData.pasajeros);
    
    const initialPassengers = [];
    for (let i = 0; i < busquedaData.pasajeros; i++) {
      initialPassengers.push({ nombre: '', apellido: '', cedula: '', telefono: '' });
    }
    setPassengerData(initialPassengers);
    setCurrentPassengerIndex(0);
    irAlPaso(3);
  };

  const handlePassengerChange = (index, field, value) => {
    const nuevos = [...passengerData];
    nuevos[index][field] = value;
    setPassengerData(nuevos);
  };

  const verificarDatosCompletos = () => {
    for (let i = 0; i < cantidadPasajeros; i++) {
      if (!passengerData[i].nombre || !passengerData[i].apellido || !passengerData[i].cedula) {
        Swal.fire({
          icon: 'warning',
          title: 'Datos incompletos',
          text: `Completá nombre, apellido y cédula del Pasajero ${i + 1}`
        });
        return false;
      }
    }
    return true;
  };

  // ✅ Generar ticket con TicketVentaPDF (estado Pagado)
  const generarTicketPDF = async (codigo, total) => {
    const servicioParaPDF = {
      origen: selectedServicio.origen,
      destino: selectedServicio.destino,
      fecha: selectedServicio.fecha,
      horaSalida: selectedServicio.horaSalida,
      empresa: selectedServicio.empresa,
      precio: selectedServicio.precio
    };

    const reservaParaPDF = {
      codigo: codigo
    };

    try {
      const blob = await pdf(
        <TicketVentaPDF
          reserva={reservaParaPDF}
          servicio={servicioParaPDF}
          passengerData={passengerData}
          cantidadPasajeros={cantidadPasajeros}
        />
      ).toBlob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket_${codigo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Error generando PDF:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo generar el ticket PDF'
      });
      return false;
    }
  };

  const handleFinalizarVenta = async () => {
    if (!verificarDatosCompletos()) return;

    const total = selectedServicio.precio * cantidadPasajeros;
    
    try {
      const todosLosNombres = passengerData.map(p => `${p.nombre} ${p.apellido}`);
      const todasLasCedulas = passengerData.map(p => p.cedula);
      
      // ✅ Venta directa: estado "Pagado" y método "Efectivo"
      const response = await fetch(`${API_URL}/admin/reservas/directa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          servicioId: selectedServicio.id,
          cantidadPasajeros: cantidadPasajeros,
          nombresPasajeros: todosLosNombres,
          cedulaTitular: passengerData[0].cedula,
          cedulasPasajeros: todasLasCedulas.slice(1),
          nombrePasajero: passengerData[0].nombre,
          emailPasajero: passengerData[0].email || `cliente_${Date.now()}@ventadirecta.com`,
          telefonoPasajero: passengerData[0].telefono || '',
          metodoPago: 'efectivo',
          comprobante: null
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        await generarTicketPDF(result.codigo, total);
        
        Swal.fire({
          title: '✅ Venta realizada',
          html: `
            <div style="text-align: left;">
              <p><strong>Número de ticket:</strong></p>
              <p class="text-2xl font-bold text-center font-mono my-2">${result.codigo}</p>
              <hr class="my-3">
              <p><strong>Total cobrado: $${formatPrecio(total)}</strong></p>
              <p><strong>Pasajeros:</strong></p>
              <ul class="list-disc pl-5">
                ${passengerData.map((p, idx) => `<li>${p.nombre} ${p.apellido} (Cédula: ${p.cedula})</li>`).join('')}
              </ul>
              <p class="mt-2"><strong>Origen - Destino:</strong> ${selectedServicio.origen} → ${selectedServicio.destino}</p>
              <p><strong>Fecha:</strong> ${new Date(selectedServicio.fecha).toLocaleDateString('es-AR')}</p>
              <p><strong>Horario:</strong> ${selectedServicio.horaSalida?.substring(0,5)} - ${selectedServicio.horaLlegada?.substring(0,5)}</p>
              <p><strong>Método de pago:</strong> Efectivo</p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Nueva venta',
          confirmButtonColor: '#1f2937'
        }).then(() => {
          setStep(1);
          setBusquedaData({ origen: '', destino: '', fecha: '', pasajeros: 1 });
          setSelectedServicio(null);
          setServicios([]);
          setPassengerData([]);
        });
      } else {
        throw new Error(result.message || 'Error al crear reserva');
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo completar la venta' });
    }
  };

  const siguientePasajero = () => {
    if (currentPassengerIndex < cantidadPasajeros - 1) {
      setCurrentPassengerIndex(currentPassengerIndex + 1);
    }
  };

  const anteriorPasajero = () => {
    if (currentPassengerIndex > 0) {
      setCurrentPassengerIndex(currentPassengerIndex - 1);
    }
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR').format(precio);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    return new Date(fecha).toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  // Componente de navegación de pasos
  const StepNavigation = () => (
    <div className="flex items-center justify-between mb-6">
      <button
        onClick={pasoAnterior}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
          step === 1
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`}
        disabled={step === 1}
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Anterior</span>
      </button>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Venta en Mostrador</h1>
          <p className="text-gray-500 text-sm">Realizá la venta de pasajes directamente</p>
        </div>

        {/* Pasos */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {[
              { num: 1, title: 'Buscar viaje', icon: Search },
              { num: 2, title: 'Elegir servicio', icon: Bus },
              { num: 3, title: 'Datos de pasajeros', icon: Users },
              { num: 4, title: 'Cobrar', icon: CreditCard }
            ].map((stepInfo) => (
              <button
                key={stepInfo.num}
                onClick={() => {
                  if (stepInfo.num < step) {
                    irAlPaso(stepInfo.num);
                  } else if (stepInfo.num === step) {
                    return;
                  } else if (stepInfo.num === 2 && step === 1) {
                    buscarServicios();
                  } else if (stepInfo.num === 3 && step === 2 && selectedServicio) {
                    irAlPaso(3);
                  } else if (stepInfo.num === 4 && step === 3 && passengerData.every(p => p.nombre && p.apellido && p.cedula)) {
                    irAlPaso(4);
                  }
                }}
                className="flex flex-col items-center flex-1 focus:outline-none group"
                disabled={
                  (stepInfo.num > step && stepInfo.num !== 2) ||
                  (stepInfo.num === 3 && !selectedServicio)
                }
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  step >= stepInfo.num 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
                } ${stepInfo.num > step && stepInfo.num !== 2 ? 'opacity-50' : ''}`}>
                  {step > stepInfo.num ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <stepInfo.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-xs mt-2 transition-colors ${
                  step >= stepInfo.num 
                    ? 'text-gray-800 font-medium' 
                    : 'text-gray-400'
                }`}>
                  {stepInfo.title}
                </span>
              </button>
            ))}
          </div>
          <div className="relative mt-4 max-w-3xl mx-auto">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full bg-gray-200 rounded-full h-1.5"></div>
            </div>
            <div className="relative flex justify-start">
              <div 
                className="bg-gray-800 h-1.5 rounded-full transition-all duration-500" 
                style={{ width: `${((step - 1) / 3) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Step 1: Búsqueda */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-lg max-w-3xl mx-auto p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Buscar viaje</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Origen</label>
                  <input
                    type="text"
                    value={busquedaData.origen}
                    onChange={handleOrigenChange}
                    onFocus={() => busquedaData.origen && setShowOrigenSugerencias(true)}
                    onBlur={() => setTimeout(() => setShowOrigenSugerencias(false), 200)}
                    placeholder="Ciudad de origen"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                    required
                    autoComplete="off"
                  />
                  {showOrigenSugerencias && origenSugerencias.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {origenSugerencias.map((ciudad) => (
                        <div
                          key={ciudad.id}
                          onClick={() => seleccionarOrigen(ciudad)}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                        >
                          <div className="text-sm font-medium text-gray-800">{ciudad.nombre}</div>
                          <div className="text-xs text-gray-500">{ciudad.estado}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="relative">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Destino</label>
                  <input
                    type="text"
                    value={busquedaData.destino}
                    onChange={handleDestinoChange}
                    onFocus={() => busquedaData.destino && setShowDestinoSugerencias(true)}
                    onBlur={() => setTimeout(() => setShowDestinoSugerencias(false), 200)}
                    placeholder="Ciudad de destino"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                    required
                    autoComplete="off"
                  />
                  {showDestinoSugerencias && destinoSugerencias.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {destinoSugerencias.map((ciudad) => (
                        <div
                          key={ciudad.id}
                          onClick={() => seleccionarDestino(ciudad)}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                        >
                          <div className="text-sm font-medium text-gray-800">{ciudad.nombre}</div>
                          <div className="text-xs text-gray-500">{ciudad.estado}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha de viaje</label>
                  <input
                    type="date"
                    min={getTodayDate()}
                    value={busquedaData.fecha}
                    onChange={(e) => setBusquedaData({...busquedaData, fecha: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Cantidad de pasajeros</label>
                  <select
                    value={busquedaData.pasajeros}
                    onChange={(e) => setBusquedaData({...busquedaData, pasajeros: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                  >
                    {[1,2,3,4,5,6].map(num => (
                      <option key={num} value={num}>{num} {num === 1 ? 'pasajero' : 'pasajeros'}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <button
                onClick={buscarServicios}
                disabled={loading}
                className="w-full mt-4 bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                {loading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white"></div> : <Search className="w-4 h-4" />}
                Buscar servicios
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Servicios disponibles */}
        {step === 2 && (
          <div>
            <StepNavigation />

            {/* Selector de fechas */}
            <div className="mb-6 overflow-x-auto">
              <div className="flex gap-3 pb-2">
                {fechasDisponibles.map((fecha, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDateChange(idx)}
                    className={`flex-shrink-0 text-center px-4 py-2 rounded-xl transition-all ${
                      selectedDateIndex === idx
                        ? 'bg-gray-800 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-xs font-medium">{fecha.dia}</div>
                    <div className="text-lg font-bold">{fecha.diaNumero}</div>
                    <div className="text-xs">{fecha.mes}</div>
                    <div className="text-xs mt-1">desde ${formatPrecio(fecha.precioMinimo)}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de servicios */}
            {loading ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto"></div>
                <p className="text-gray-500 mt-3">Cargando servicios...</p>
              </div>
            ) : servicios.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Bus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay servicios disponibles para esta fecha</p>
              </div>
            ) : (
              <div className="space-y-3">
                {servicios.map((servicio) => (
                  <div key={servicio.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 flex-wrap">
                          <div>
                            <div className="text-xs text-gray-500">Salida</div>
                            <div className="text-xl font-bold">{servicio.horaSalida?.substring(0,5)}</div>
                            <div className="text-xs text-gray-500">{servicio.origen}</div>
                          </div>
                          <div className="text-gray-400">→</div>
                          <div>
                            <div className="text-xs text-gray-500">Llegada</div>
                            <div className="text-xl font-bold">{servicio.horaLlegada?.substring(0,5)}</div>
                            <div className="text-xs text-gray-500">{servicio.destino}</div>
                          </div>
                          <div className="border-l pl-4">
                            <div className="text-sm font-semibold">{servicio.empresa}</div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Precio por persona</div>
                        <div className="text-2xl font-bold text-gray-800">${formatPrecio(servicio.precio)}</div>
                        <button
                          onClick={() => seleccionarServicio(servicio)}
                          className="mt-2 bg-gray-800 hover:bg-gray-900 text-white px-4 py-1.5 rounded-lg text-sm flex items-center gap-1"
                        >
                          Seleccionar <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Datos de pasajeros */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <StepNavigation />

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800">Datos de los pasajeros</h3>
                  <span className="text-sm text-gray-500">
                    Pasajero {currentPassengerIndex + 1} de {cantidadPasajeros}
                  </span>
                </div>
                <div className="flex gap-1">
                  {Array.from({ length: cantidadPasajeros }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPassengerIndex(idx)}
                      className={`flex-1 h-2 rounded-full transition-all ${
                        idx === currentPassengerIndex
                          ? 'bg-gray-800'
                          : passengerData[idx]?.nombre && passengerData[idx]?.apellido && passengerData[idx]?.cedula
                          ? 'bg-green-500'
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold">
                    {currentPassengerIndex + 1}
                  </div>
                  <h4 className="font-medium text-gray-800">
                    Pasajero {currentPassengerIndex + 1}
                  </h4>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={passengerData[currentPassengerIndex]?.nombre || ''}
                        onChange={(e) => handlePassengerChange(currentPassengerIndex, 'nombre', e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                        placeholder="Juan"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Apellido <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={passengerData[currentPassengerIndex]?.apellido || ''}
                      onChange={(e) => handlePassengerChange(currentPassengerIndex, 'apellido', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                      placeholder="Pérez"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                      <IdCard className="w-3 h-3" />
                      Cédula <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={passengerData[currentPassengerIndex]?.cedula || ''}
                      onChange={(e) => handlePassengerChange(currentPassengerIndex, 'cedula', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                      placeholder="12.345.678-9"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      <Phone className="w-3 h-3 inline mr-1" />
                      Teléfono (opcional)
                    </label>
                    <input
                      type="tel"
                      value={passengerData[currentPassengerIndex]?.telefono || ''}
                      onChange={(e) => handlePassengerChange(currentPassengerIndex, 'telefono', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-gray-400 focus:outline-none"
                      placeholder="11 1234 5678"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mb-6">
                <button
                  onClick={anteriorPasajero}
                  disabled={currentPassengerIndex === 0}
                  className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                    currentPassengerIndex === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>
                {currentPassengerIndex < cantidadPasajeros - 1 ? (
                  <button
                    onClick={siguientePasajero}
                    className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-gray-900"
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => irAlPaso(4)}
                    className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    Continuar
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Resumen del viaje</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Servicio:</span>
                    <span className="font-medium">{selectedServicio?.origen} → {selectedServicio?.destino}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha:</span>
                    <span className="font-medium">{formatFecha(selectedServicio?.fecha)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Horario:</span>
                    <span className="font-medium">{selectedServicio?.horaSalida?.substring(0,5)} - {selectedServicio?.horaLlegada?.substring(0,5)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-gray-200 mt-2">
                    <span>Total a cobrar:</span>
                    <span>${formatPrecio(selectedServicio?.precio * cantidadPasajeros)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Cobrar */}
        {step === 4 && (
          <div className="max-w-2xl mx-auto">
            <StepNavigation />

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmar cobro</h3>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Resumen de la venta</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Servicio:</span>
                    <span className="font-medium">{selectedServicio?.origen} → {selectedServicio?.destino}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha:</span>
                    <span className="font-medium">{formatFecha(selectedServicio?.fecha)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Horario:</span>
                    <span className="font-medium">{selectedServicio?.horaSalida?.substring(0,5)} - {selectedServicio?.horaLlegada?.substring(0,5)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pasajeros:</span>
                    <span className="font-medium">{cantidadPasajeros}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 text-white rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total a cobrar</span>
                  <span className="text-3xl font-bold">${formatPrecio(selectedServicio?.precio * cantidadPasajeros)}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => irAlPaso(3)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Volver
                </button>
                <button
                  onClick={handleFinalizarVenta}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Cobrar y emitir ticket
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default VentaDirecta;